// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getSelectedProductPath,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { parseBoolean, safeJsonParse } from "@sc/XDL/Utilities";
import { getCurrentPage, getLocation, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductsDataView,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { DispatchSetFilterQuery } from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AttributeTypeFacetModel,
    CartModel,
    CategoryFacetModel,
    FacetModel,
    ProductInventoryDto,
    ProductModel,
    ProductPriceDto,
    SuggestionModel,
} from "@insite/client-framework/Types/ApiModels";
import { xGoogleAnalyticsState, xGoogleAnalyticsStateType } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { extractFacetData } from "@sc/App/GoogleAnalyticsConnector/Utilities/extractFacetData";
import { checkoutPages } from "@sc/XDL/constants";

// Type definition for the product data used in Google Analytics
type ProductData = Pick<
    ProductModel,
    "productNumber" | "productTitle" | "brand" | "properties" | "minimumOrderQty" | "quoteRequired" | "canonicalUrl"
> & {
    pricing: ProductPriceDto | undefined;
    inventory: ProductInventoryDto | undefined;
};

// Interface definition for the state structure used in Google Analytics connector
interface GoogleAnalyticsConnectorMapState {
    correctedQuery: string;
    originalQuery: string;
    numberOfResults: number;
    didYouMeanSuggestions?: SuggestionModel[];
    query?: string;
    searchPath?: string;
    cart?: CartModel;
    isPDP?: boolean;
    isPLP?: boolean;
    isCartPage?: boolean;
    isInCheckoutPage?: boolean;
    xGoogleAnalytics?: xGoogleAnalyticsStateType;
    currentPage?: string;
    currencyCode?: string;
    affiliation?: string;
    locationId?: string;
    PLPProducts?: ProductData[];
    PDPProduct?: ProductData | null;
    userEmail?: string;
    billToNum?: string;
    shipToNum?: string;
    salesRep?: string;
    isRep?: string;
    customerType?: string;
    loginType?: string;
    warehouse?: string;
}

// Interface definition for the facet data structure
interface FacetData {
    facetField?: string;
    facetId?: string;
    facetTitle?: string;
    facetValue?: string | boolean;
    resultsSortBy?: string;
    itemsCount?: number;
}

// Function to map application state to Google Analytics connector state
export const getGoogleAnalyticsConnectorMapState = (state: ApplicationState): GoogleAnalyticsConnectorMapState => {
    const productsDataView = getProductListDataView(state);
    let PLPProducts: ProductData[] = [];
    let PDPProduct: ProductData | null = null;

    const currentPage = getCurrentPage(state).type;
    const location = getLocation(state);
    const isPDP = currentPage === "ProductDetailsPage";
    const isPLP = currentPage === "ProductListPage";
    const isCartPage = currentPage === "CartPage";
    const isInCheckoutPage = checkoutPages.includes(currentPage)

    // Populate PLP products if the current page is Product List Page
    if (isPLP) {
        const {
            pages: {
                productList: { parameter },
            },
        } = state;
        const productsDataView = getProductsDataView(state, parameter);
        if (productsDataView?.value) {
            PLPProducts = productsDataView.value.map(product => {
                const { productNumber, productTitle, brand, properties, minimumOrderQty, quoteRequired, canonicalUrl } =
                    product;
                const productInfo = state.pages.productList.productInfosByProductId[product.id];
                return {
                    productNumber,
                    productTitle,
                    brand,
                    properties,
                    pricing: productInfo?.pricing,
                    minimumOrderQty,
                    inventory: productInfo?.inventory,
                    quoteRequired,
                    canonicalUrl,
                };
            });
        }
    }

    // Populate PDP product if the current page is Product Details Page
    if (isPDP) {
        const productPath =
            getSelectedProductPath(state) ||
            (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
        const productState = getProductStateByPath(state, productPath);
        const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
        const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
        const productInfo =
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined;

        if (productState?.value) {
            const { productNumber, productTitle, brand, properties, minimumOrderQty, quoteRequired, canonicalUrl } =
                productState.value;
            PDPProduct = {
                productNumber,
                productTitle,
                brand,
                properties,
                pricing: productInfo?.pricing as ProductPriceDto,
                minimumOrderQty,
                inventory: productInfo?.inventory,
                quoteRequired,
                canonicalUrl,
            };
        }
    }

    const session = getSession(state);
    const billToState = getBillToState(state, session.billToId);
    const shipToState = getShipToState(state, session.shipToId);

    // Construct the result object with mapped state values
    const result: GoogleAnalyticsConnectorMapState = {
        correctedQuery: "",
        originalQuery: "",
        numberOfResults: 0,
        didYouMeanSuggestions: [],
        query: getQueryStrings(state).query,
        searchPath: getSettingsCollection(state).searchSettings.searchPath,
        cart: getCurrentCartState(state).value,
        isPDP,
        isPLP,
        isCartPage,
        isInCheckoutPage,
        xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
        currentPage,
        currencyCode: state.context?.session?.currency?.currencyCode,
        affiliation:
            session.fulfillmentMethod === "PickUp"
                ? session.pickUpWarehouse?.description
                : billToState.value?.companyName,
        locationId: session.pickUpWarehouse?.id,
        PLPProducts,
        PDPProduct,
        userEmail: session.email,
        billToNum: billToState.value?.customerNumber,
        shipToNum: shipToState.value?.customerSequence,
        salesRep: safeJsonParse(session.properties?.salespersonInfo)?.["name"],
        isRep: parseBoolean(session.properties?.isRep) ? "true" : "false",
        customerType: session.properties?.customerType,
        loginType: session.properties?.loginType,
        warehouse: session.properties?.shipToWarehouse,
    };

    // Populate additional search data if available
    if (productsDataView.value) {
        result.correctedQuery = productsDataView.correctedQuery;
        result.originalQuery = productsDataView.originalQuery;
        result.numberOfResults = productsDataView.pagination?.totalItemCount || result.numberOfResults;
        result.didYouMeanSuggestions = productsDataView.didYouMeanSuggestions as SuggestionModel[];
    }

    return result;
};

// Function to get query from URL
export const getQueryFromUrl = (state: ApplicationState) => {
    const { search } = state.data.pages.location;
    const query = parseQueryString<{ query?: string }>(search);
    return query;
};

/**
 * Function to extract facet data
 * @param props of type PropsType
 * @param attributeTypeFacets
 * @param categoryFacets
 * @param productLineFacets
 * @param brandFacets
 * @returns object of {facetField, facetId, facetTitle, facetValue}
 */
export const getFacetData = (
    props: Parameters<typeof DispatchSetFilterQuery>[0],
    attributeTypeFacets: AttributeTypeFacetModel[] | null | undefined,
    categoryFacets: CategoryFacetModel[] | null | undefined,
    productLineFacets: FacetModel[] | null | undefined,
    brandFacets: FacetModel[] | null | undefined,
): FacetData => {
    const {
        attributeValueIds,
        brandIds,
        categoryId,
        productLineIds,
        stockedItemsOnly,
        previouslyPurchasedProducts,
        sort,
    } = props.parameter;

    // Determine facet data based on parameters
    // And handling different cases of facet data
    if (sort) {
        return {
            resultsSortBy: sort,
        };
    }
    if (attributeValueIds) {
        const facetType = attributeTypeFacets?.find(a =>
            a.attributeValueFacets?.some(
                innerAttribute => innerAttribute.attributeValueId === extractFacetData(attributeValueIds),
            ),
        );
        const facetValue = facetType?.attributeValueFacets?.find(
            facet => facet.attributeValueId === extractFacetData(attributeValueIds),
        );
        return {
            facetField: facetType?.name || "",
            facetId: extractFacetData(attributeValueIds),
            facetTitle: facetValue?.valueDisplay || "",
            facetValue: extractFacetData(attributeValueIds),
            itemsCount: facetValue?.count,
        };
    }
    if (brandIds) {
        const brandId = extractFacetData(brandIds);
        const brand = brandFacets?.find(b => b.id === brandId);
        return {
            facetField: translate("Brand"),
            facetId: brandId,
            facetTitle: brandId,
            facetValue: brandId,
            itemsCount: brand?.count,
        };
    }
    if (categoryId) {
        const category = categoryFacets?.find(c => c.categoryId === categoryId);
        return {
            facetField: translate("Category"),
            facetId: categoryId,
            facetTitle: category?.shortDescription || "",
            facetValue: categoryId,
            itemsCount: category?.count,
        };
    }
    if (productLineIds) {
        const productLine = productLineFacets?.find(p => p.id === extractFacetData(productLineIds));
        return {
            facetField: translate("Product Lines"),
            facetId: extractFacetData(productLineIds),
            facetTitle: productLine?.name || "",
            facetValue: extractFacetData(productLineIds),
            itemsCount: productLine?.count,
        };
    }
    if (stockedItemsOnly) {
        return {
            facetField: translate("Stocked Items Only"),
            facetTitle: translate("Stocked Items Only"),
            facetValue: false,
        };
    }
    if (previouslyPurchasedProducts) {
        return {
            facetField: translate("Previously Purchased Products"),
            facetTitle: translate("Previously Purchased Products"),
            facetValue: false,
        };
    }

    return {
        facetField: "",
        facetId: "",
        facetTitle: "",
        facetValue: false,
    };
};
